import {
  Box,
  Text,
  Grid,
  GridItem,
  Flex,
  Divider,
  Select,
} from '@chakra-ui/react'
import Image from 'next/legacy/image'
import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { CheckIcon } from '@chakra-ui/icons'
import { useTranslation } from 'next-i18next'
import { isNil } from 'lodash-es'
import { engraveOptionName } from 'modules/product/utils/engraveOptionName'
import { EngraveTabComponentProps } from '_app/types/product'
import getCurrency from '_app/utils/localized/getCurrency'
import PriceText from '_app/components/PriceText'
import { useRouter } from '_app/hooks/useRouter'
import theme from 'theme'
import {
  ComponentEngravingEngraving,
  EngravingEntity,
  Maybe,
} from '_app/types/cms'

const Graphic = ({
  option,
  onAddOption,
  engravingsData,
}: EngraveTabComponentProps) => {
  const { t } = useTranslation('engraving')
  const { locale } = useRouter()
  const { watch, setValue } = useFormContext()
  const [categoryTitle, setCategoryTitle] = useState(
    engravingsData[0].attributes?.CategoryName
  )
  const inputName = `${engraveOptionName(option)}.file`
  const inputValue = watch(inputName)

  const onSelect = (src: string) => {
    setValue(inputName, src)
    onAddOption()
  }

  const onRemove = () => {
    setValue('selected_engrave_options', undefined)
    setValue(inputName, null)
  }

  const [gridItem, setGridItem] = useState<
    Maybe<ComponentEngravingEngraving>[]
  >(
    engravingsData.find(
      (engravingData: EngravingEntity) =>
        engravingData.attributes?.CategoryName === categoryTitle
    )?.attributes?.Engravings ?? []
  )

  useEffect(() => {
    setGridItem(
      engravingsData.find(
        (engravingData: EngravingEntity) =>
          engravingData.attributes?.CategoryName === categoryTitle
      )?.attributes?.Engravings ?? []
    )
  }, [categoryTitle])

  return (
    <Box>
      <Text
        mb={3}
        mt={{ base: 2, md: 0.5 }}
        fontWeight={300}
        flexDirection="row"
        display="flex"
      >
        {t('ENGRAVING_GRAPHIC_PRICE', { ns: 'engraving' })}:
        {!isNil(option.value?.price) && (
          <Text pl={1} fontWeight={400}>
            <PriceText
              amount={{
                value: option.value?.price,
                currency: getCurrency(locale),
              }}
              floating
            />
          </Text>
        )}
      </Text>
      <Divider mb={6} />
      <Select
        value={categoryTitle}
        name={categoryTitle?.toString()}
        onChange={e => setCategoryTitle(e.target.value)}
        defaultValue={1}
        sx={{
          borderWidth: '0 0 1px 0',
          borderColor: theme.colors.grey,
          borderRadius: 0,
          padding: 0,
          ':focus': {
            borderColor: theme.colors.grey,
          },
          ':hover': {
            borderColor: theme.colors.grey,
          },
        }}
      >
        {engravingsData.map(engravingData => (
          <option
            key={engravingData.attributes?.CategoryName}
            value={engravingData.attributes?.CategoryName}
          >
            {/* related to backwards-compatibility - we fallback to initial data in case of CMS data error*/}
            {engravingData.attributes?.needsTranslation
              ? t(engravingData.attributes?.categoryNameKey ?? '', {
                  ns: 'engraving',
                })
              : engravingData.attributes?.CategoryName}
          </option>
        ))}
      </Select>
      {gridItem && (
        <Box key={categoryTitle} pb={6} mb={6}>
          <Grid
            templateColumns={{
              base: 'repeat(2, 1fr)',
              xs: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)',
            }}
            gap={4}
            pt={6}
          >
            {gridItem.map((engraving: Maybe<ComponentEngravingEngraving>) => {
              const src = engraving?.Image.data?.attributes?.url ?? ''
              const srcToSend =
                engraving?.ImageToSend?.data?.attributes?.url ?? ''
              const isSelected = inputValue === srcToSend

              return (
                <GridItem key={src}>
                  <Box
                    role="button"
                    cursor="pointer"
                    position="relative"
                    onClick={() => isSelected ? onRemove() : onSelect(srcToSend)}
                    borderWidth="1px"
                    borderColor={isSelected ? 'black' : 'transparent'}
                  >
                    <Image
                      src={src}
                      alt=""
                      width="130"
                      height="130"
                      layout="responsive"
                    />
                    {isSelected && (
                      <Flex
                        position="absolute"
                        bottom={2}
                        right={2}
                        bg="black"
                        rounded="full"
                        boxSize={4}
                        justifyContent="center"
                        alignItems="center"
                      >
                        <CheckIcon
                          color="white"
                          fontSize="8px"
                          fontWeight={600}
                        />
                      </Flex>
                    )}
                  </Box>
                </GridItem>
              )
            })}
          </Grid>
        </Box>
      )}
    </Box>
  )
}

export default Graphic
